import React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from '@/components/common/text';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import type { ItemStatus } from '@/types/admin/todoLists';

interface Props {
  status: ItemStatus;
  onChange: (status: ItemStatus) => void;
  disabled?: boolean;
}

/** The three SELECTABLE statuses, in display order. 'todo' has no pill. */
type SelectableStatus = Exclude<ItemStatus, 'todo'>;
const OPTIONS: ReadonlyArray<{ status: SelectableStatus; label: string }> = [
  { status: 'in_progress', label: 'In progress' },
  { status: 'done', label: 'Done' },
  { status: 'skipped', label: 'Skipped' },
];

/**
 * ItemStatusSelect — a three-pill status control: [In progress] [Done] [Skipped].
 *
 * 'todo' is the IMPLICIT default and has NO pill. When `status === 'todo'` none
 * of the three pills is highlighted (all render as transparent, card.border
 * outlines with muted text). When `status` is one of the three, that pill is
 * highlighted with its status token pair (its border color equals its bg).
 *
 * Tapping a NON-highlighted pill emits `onChange(<that status>)`. Tapping the
 * HIGHLIGHTED pill emits `onChange('todo')` — the revert/undo path, and the ONLY
 * way 'todo' is ever emitted. `disabled` blocks every tap and dims the group to
 * 0.5 opacity.
 *
 * The highlighted pill's tokens (bg / text):
 *   in_progress  status.inProgress / status.inProgressText
 *   done         accent.greenBg    / accent.green
 *   skipped      accent.redBg      / accent.red
 *
 * This is NOT a radio group (a pill can be tapped to deselect back to 'todo'), so
 * the container is a plain View and each pill is a "button" with
 * accessibilityState.selected reflecting the current status.
 */
export const ItemStatusSelect: React.FC<Props> = ({
  status,
  onChange,
  disabled = false,
}) => {
  const { redesign } = useAdminColors();

  // Token pair used to highlight a pill when it is the current status.
  const activeTokens: Record<
    SelectableStatus,
    { backgroundColor: string; color: string }
  > = {
    in_progress: {
      backgroundColor: redesign.status.inProgress,
      color: redesign.status.inProgressText,
    },
    done: {
      backgroundColor: redesign.accent.greenBg,
      color: redesign.accent.green,
    },
    skipped: {
      backgroundColor: redesign.accent.redBg,
      color: redesign.accent.red,
    },
  };

  return (
    <View
      testID="status-select-group"
      style={[styles.group, disabled && styles.disabled]}
    >
      {OPTIONS.map(({ status: optionStatus, label }) => {
        const isCurrent = optionStatus === status;
        const active = activeTokens[optionStatus];
        // Highlighted pill: filled with its status tokens (border == bg). Others:
        // transparent bg, card.border outline, muted text.
        const backgroundColor = isCurrent
          ? active.backgroundColor
          : 'transparent';
        const borderColor = isCurrent
          ? active.backgroundColor
          : redesign.card.border;
        const textColor = isCurrent ? active.color : redesign.text.muted;

        const handlePress = (): void => {
          if (disabled) return;
          // Tapping the highlighted pill reverts to the implicit 'todo' default;
          // tapping any other pill selects that status.
          onChange(isCurrent ? 'todo' : optionStatus);
        };

        return (
          <TouchableOpacity
            key={optionStatus}
            testID={`status-option-${optionStatus}`}
            accessibilityRole="button"
            accessibilityState={{ selected: isCurrent, disabled }}
            accessibilityLabel={
              isCurrent ? `Undo ${label} — back to To do` : label
            }
            disabled={disabled}
            onPress={handlePress}
            style={[styles.pill, { backgroundColor, borderColor }]}
          >
            <Text style={[styles.pillText, { color: textColor }]}>{label}</Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  disabled: { opacity: 0.5 },
  // flexWrap lets the pills reflow within the cluster on very narrow widths.
  group: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  pill: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 14,
    borderWidth: 1,
  },
  pillText: { fontSize: 11, fontWeight: '600' },
});
